import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FounderOperationalShell,
  type FounderMetric,
} from "../../../components/founder/FounderOperationalShell";
import {
  FounderErrorState,
  FounderLoadingState,
} from "../../../components/founder/FounderWorkspaceChrome";
import { fetchFounderSecurityAlerts } from "../../../founder/founderWorkspaceApi";
import {
  fetchFounderHomeDashboard,
  formatPublicationModeLabel,
  type FounderHomeDashboardData,
} from "../../../services/founderHomeDashboard";
import {
  buildOverviewSecurityAttentionItems,
  buildOverviewSecurityHealthItem,
  type OverviewSecurityAlert,
} from "./founderOverviewSecurityPresentation";

function formatWhen(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return "Recently";
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function FounderOverviewWorkspace() {
  const [dashboard, setDashboard] = useState<FounderHomeDashboardData | null>(null);
  const [securityAlerts, setSecurityAlerts] = useState<OverviewSecurityAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchFounderHomeDashboard(), fetchFounderSecurityAlerts(5)])
      .then(([dashboardData, alerts]) => {
        if (!cancelled) {
          setDashboard(dashboardData);
          setSecurityAlerts(alerts as OverviewSecurityAlert[]);
          setError("");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Could not load overview data.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const counts = dashboard?.counts;
  const metrics: FounderMetric[] = counts
    ? [
        { id: "pending", label: "Pending reviews", value: counts.applicationsPendingReview, tone: "coral" },
        { id: "published", label: "Published apps", value: counts.publishedApplications, tone: "mint" },
        { id: "publishers", label: "Active publishers", value: counts.activePublishers, tone: "steel" },
        { id: "memberships", label: "Active memberships", value: counts.activeSubscriptions, tone: "teal" },
        { id: "payments", label: "Payment issues", value: counts.paymentFailures, tone: counts.paymentFailures > 0 ? "coral" : "sand" },
      ]
    : [];

  const attentionItems = useMemo(() => {
    if (!dashboard) {
      return [];
    }

    const withoutGenericSecurity = dashboard.attentionItems.filter(
      (item) => item.id !== "security-alerts",
    );
    return [...withoutGenericSecurity, ...buildOverviewSecurityAttentionItems(securityAlerts)];
  }, [dashboard, securityAlerts]);

  const platformHealth = useMemo(() => {
    if (!dashboard) {
      return [];
    }

    return dashboard.platformHealth.map((item) =>
      item.id === "security"
        ? buildOverviewSecurityHealthItem(securityAlerts, item)
        : item,
    );
  }, [dashboard, securityAlerts]);

  return (
    <FounderOperationalShell metrics={metrics} showTitle={false}>
      {loading ? <FounderLoadingState label="Loading overview..." /> : null}
      {!loading && error ? <FounderErrorState message={error} /> : null}

      {!loading && !error && dashboard && counts ? (
        <div className="oac-founder-ops-grid oac-founder-ops-grid--2col">
          <section className="oac-founder-ops-panel">
            <h2 className="oac-founder-ops-panel__title">Attention required</h2>
            {attentionItems.length === 0 ? (
              <p className="oac-founder-ops-panel__empty">Nothing urgent right now.</p>
            ) : (
              <ul className="oac-founder-ops-list">
                {attentionItems.map((item) => (
                  <li key={item.id}>
                    <Link className="oac-founder-ops-list__item oac-founder-ops-list__item--link" to={item.href}>
                      <span className="oac-founder-ops-list__title">{item.label}</span>
                      <span className="oac-founder-ops-list__meta">{item.detail}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="oac-founder-ops-panel">
            <h2 className="oac-founder-ops-panel__title">Platform health</h2>
            <ul className="oac-founder-ops-list">
              {platformHealth.map((item) => (
                <li className="oac-founder-ops-list__item" key={item.id}>
                  <div>
                    <span className="oac-founder-ops-list__title">{item.label}</span>
                    <span className="oac-founder-ops-list__meta">{item.detail}</span>
                  </div>
                  <span className={`oac-founder-badge oac-founder-badge--${item.status === "healthy" ? "healthy" : "attention"}`}>
                    {item.status === "healthy" ? "Healthy" : "Attention"}
                  </span>
                </li>
              ))}
            </ul>
            <p className="oac-founder-ops-footnote">
              Publishing mode: {formatPublicationModeLabel(counts.publicationMode)}
            </p>
          </section>

          <section className="oac-founder-ops-panel oac-founder-ops-panel--wide">
            <h2 className="oac-founder-ops-panel__title">Recent activity</h2>
            {dashboard.recentActivity.length === 0 ? (
              <p className="oac-founder-ops-panel__empty">No recent activity.</p>
            ) : (
              <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
                {dashboard.recentActivity.slice(0, 12).map((item) => (
                  <li className="oac-founder-ops-list__item" key={item.id}>
                    <span className="oac-founder-ops-list__title">{item.label}</span>
                    <time className="oac-founder-ops-list__meta" dateTime={item.occurredAt}>
                      {formatWhen(item.occurredAt)}
                    </time>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : null}
    </FounderOperationalShell>
  );
}
